import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import { useMeasure } from 'react-use';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { deleteUndefinedValues } from '@/utils/objects';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import { hasAnySelectionData } from '../../utils/selections';
import { extractDataLabelProperty, getSectionCanvasId } from '../internal/utils';
import useData from './useData';
import Header from '../misc/Header';
import settings, { selectionConfig, labelsSelectionConfig } from './settings';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../misc/StyleVariables';
import useSelections from '@/components/widget/misc/useSelections';
import useSelectionLabels from '@/components/widget/misc/useSelectionLabels';
import { selectEditingArea } from '@/components/Editor/hooks/useEditingAreas';
import useSubscribeWidgetResults from '../hooks/useSubscribeWidgetResults';
import useSingleSelection from '../hooks/useSingleSelection';
import { overrideConfig } from './utils';
import { usePieDoughnutStyles } from './stylesTheme';
import useCenterLabels from './useCenterLabels';
import BarsLegend, { BarsLegendItem } from './BarsLegend';
import Loading from './Loading';
import styles from './styles.module.scss';
import useChartActions from '../internal/useChartActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const PieDoughnut = _ref => {
  var _configWithOverrides$, _configWithOverrides$2, _config$styles4;

  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    elementId,
    editing = false,
    config
  } = _ref;
  const [containerRef, {
    width: containerWidth
  }] = useMeasure();
  const {
    themeStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = usePieDoughnutStyles(config);
  const configWithOverrides = useMemo(() => overrideConfig(config), [config]);
  const {
    selections = '',
    labelsSelections = '',
    selectionsStrategy = 'default',
    selectionsLabeled = true,
    seriesOptions = [],
    globalSeriesOptions,
    colorPallet = defaultColorPallet,
    collectionOptions = [],
    chartType = 'doughnut',
    legendType = 'default',
    legendPosition,
    centerValueSelection = '',
    centerValueTemplate = '',
    centerLabelSelection = '',
    centerLabelTemplate = '',
    chartBackground,
    chartTitle,
    animation
  } = configWithOverrides;
  const canvasElement = useRef(null);
  const canvasId = useRef(null);
  const results = useSubscribeWidgetResults(reportingWidgetId); // Get relevant results for each selection group

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections,
    selectionsLabeled,
    selectionConfig
  });
  const {
    selectionLabels
  } = useSelectionLabels({
    results,
    resultsUpdated,
    labelsSelectionConfig,
    labelsSelections,
    labelsTemplate: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.labelsTemplate
  }); // Combine results with rendering styles

  const {
    chartData,
    legendData
  } = useData({
    results: selectionResults,
    selectionLabels,
    seriesOptions,
    globalSeriesOptions,
    collectionOptions,
    colorPallet,
    legends: configWithOverrides.legends,
    selectionsStrategy,
    themeStyles
  }); // Single Value Selections

  const centerValue = useSingleSelection({
    results,
    selection: chartType === 'doughnut' ? centerValueSelection : '',
    template: centerValueTemplate
  });
  const centerLabel = useSingleSelection({
    results,
    selection: chartType === 'doughnut' ? centerLabelSelection : '',
    template: centerLabelTemplate
  });
  const {
    config: doughnutlabel,
    redrawHash: doughnutLabelRedrawHash
  } = useCenterLabels({
    centerValue,
    centerLabel
  });
  const {
    openLegendsContextEditor
  } = useChartActions({});
  const {
    0: chart,
    1: setChart
  } = useState(null);
  const {
    0: isLoadingDeps,
    1: setIsLoadingDeps
  } = useState(false);
  const redrawIndex = [chartType, chartTitle, legendType, ...chartData.labels, chartData.datasets.length, ...chartData.datasets.map(ds => `${ds.data.map(_ref2 => {
    let {
      value,
      selectionIndex
    } = _ref2;
    return `${value},${selectionIndex}`;
  })}${ds.titles.join(',')}${ds.backgroundColor.join(',')}${ds.templates.join(',')}${ds.datalabels.map(dataLabel => Object.values(dataLabel).join(','))}`), collectionOptions.map(collectionOpt => collectionOpt.template).join(','), Object.values(themeStyles).join(','), configWithOverrides === null || configWithOverrides === void 0 ? void 0 : (_configWithOverrides$ = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$ === void 0 ? void 0 : (_configWithOverrides$2 = _configWithOverrides$.dataLabels) === null || _configWithOverrides$2 === void 0 ? void 0 : _configWithOverrides$2.displayPercentages].join('');
  const hasAnyData = chartData.datasets.some(ds => (ds.data || []).length > 0);
  const isBarsLegend = legendType === 'bars';
  const canRenderLegend = isBarsLegend && hasAnyData; // Data & Loading States

  const hasNoData = !hasAnySelectionData(selectionResults);
  const isLoading = !results.length; // Chart Setup

  useEffect(() => {
    const hasDrawableData = Boolean(redrawIndex);

    if (canvasId.current !== null || !canvasElement.current || !hasDrawableData || isLoadingDeps) {
      return;
    }

    setIsLoadingDeps(true);

    (async () => {
      var _config$styles;

      const {
        default: Chart,
        Title
      } = await import('chart.js/auto');
      const {
        default: DoughnutLabel
      } = await import('chartjs-plugin-doughnutlabel-rebourne');
      const {
        default: ChartDataLabels
      } = await import('chartjs-plugin-datalabels');
      const {
        default: ChartAnnotation
      } = await import('chartjs-plugin-annotation');
      const {
        default: CustomBackground
      } = await import('../plugins/customBackground');
      const {
        default: ResponsiveResizePlugin
      } = await import('../plugins/responsiveResizePlugin');
      const {
        default: LegendPaddingPlugin
      } = await import('../plugins/legendPadding');
      const isAnimationDisabled = (animation === null || animation === void 0 ? void 0 : animation.enabled) !== undefined && !(animation !== null && animation !== void 0 && animation.enabled); // @ts-ignore

      const chart = new Chart(canvasElement.current, {
        // @ts-ignore
        type: chartType,
        data: chartData,
        animation: _objectSpread(_objectSpread({}, deleteUndefinedValues(animation)), {}, {
          duration: isAnimationDisabled ? 0 : animation === null || animation === void 0 ? void 0 : animation.duration
        }),
        // @ts-ignore
        plugins: [DoughnutLabel, Title, ChartDataLabels, ChartAnnotation, CustomBackground, ResponsiveResizePlugin, LegendPaddingPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          borderWidth: themeStyles.arcBorderWidth,
          plugins: {
            title: chartTitle ? {
              display: true,
              text: chartTitle,
              font: {
                family: themeStyles.titleFontFamily,
                size: themeStyles.titleFontSize,
                lineHeight: themeStyles.titleLineHeight
              },
              color: themeStyles.titleColor
            } : {},
            datalabels: {
              display: true,
              font: {
                family: themeStyles.chartDataLabelFontFamily,
                size: themeStyles.chartDataLabelFontSize,
                fontWeight: themeStyles.chartDataLabelFontWeight
              },
              color: context => extractDataLabelProperty(context, 'color', themeStyles.chartDataLabelColor),
              align: context => extractDataLabelProperty(context, 'align', 'center'),
              anchor: context => extractDataLabelProperty(context, 'anchor', 'end'),
              rotation: context => extractDataLabelProperty(context, 'rotation', 0),
              clamp: context => extractDataLabelProperty(context, 'clamp', true),
              formatter: function (value, context) {
                var _context$dataset$data;

                // Hide labels if not enabled or with value 0
                const dataLabel = (_context$dataset$data = context.dataset.datalabels) === null || _context$dataset$data === void 0 ? void 0 : _context$dataset$data[context.dataIndex];
                if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display) || value === 0) return '';
                return dataLabel.template ?? value;
              }
            },
            legend: _objectSpread({
              display: legendType === 'default',
              labels: {
                usePointStyle: Boolean(themeStyles.chartLegendPointStyle),
                pointStyle: themeStyles.chartLegendPointStyle,
                boxWidth: themeStyles.chartLegendBoxWidth,
                boxHeight: themeStyles.chartLegendBoxHeight,
                padding: themeStyles.chartLegendPadding,
                font: {
                  family: themeStyles.legendFontFamily,
                  size: themeStyles.legendFontSize,
                  lineHeight: themeStyles.legendLineHeight
                },
                color: themeStyles.legendColor
              },
              // @ts-ignore
              position: getLegendPosition(containerWidth, legendPosition)
            }, editing && openLegendsContextEditor && {
              onClick: e => openLegendsContextEditor(e, widgetId, sectionId)
            }),
            legendPadding: {
              padding: 15 // Custom padding between legend and chart

            },
            tooltip: {
              enabled: true,
              usePointStyle: true,
              cornerRadius: 0,
              callbacks: {
                title: _ref3 => {
                  let [item] = _ref3;
                  // @ts-ignore
                  return (item === null || item === void 0 ? void 0 : item.dataset.titles[item.dataIndex]) || item.label;
                },
                label: item => {
                  return ` ${// @ts-ignore
                  (item === null || item === void 0 ? void 0 : item.dataset.templates[item.dataIndex]) || item.parsed.y}`;
                }
              }
            },
            doughnutlabel,
            customCanvasBackgroundColor: {
              color: (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || themeStyles.chartBackgroundColor
            }
          }
        }
      }); // Disable datalabels by default

      if (Chart.defaults.plugins.datalabels) {
        Chart.defaults.plugins.datalabels.display = false;
      }

      canvasId.current = chart.id;
      setChart(chart);
      setIsLoadingDeps(false);
    })(); // Chart teardown


    return () => {
      if (chart) {
        try {
          chart.destroy();
          setChart(null);
        } catch (err) {} // eslint-disable-line

      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId.current, redrawIndex]); // Chart Updating

  useEffect(() => {
    var _config$styles2;

    if (!chart) return;
    const isDefaultLegend = legendType === 'default';
    const hasDefaultLegend = chart.options.plugins.legend.display;

    if (chart.config.type !== chartType) {
      chart.config.type = chartType;
    }

    if (hasDefaultLegend !== isDefaultLegend) {
      chart.options.plugins.legend.display = isDefaultLegend;
    } // Update animation


    if ((animation === null || animation === void 0 ? void 0 : animation.duration) !== chart.options.animation.duration) {
      chart.options.animation.duration = animation === null || animation === void 0 ? void 0 : animation.duration;
    }

    if ((animation === null || animation === void 0 ? void 0 : animation.delay) !== chart.options.animation.delay) {
      chart.options.animation.delay = animation === null || animation === void 0 ? void 0 : animation.delay;
    }

    if ((animation === null || animation === void 0 ? void 0 : animation.easing) !== chart.options.animation.easing) {
      chart.options.animation.easing = animation === null || animation === void 0 ? void 0 : animation.easing;
    }

    if ((animation === null || animation === void 0 ? void 0 : animation.enabled) !== undefined) {
      chart.options.animation.duration = animation.enabled ? animation.duration : 0;
    } // Update chart title


    if (chartTitle !== chart.options.plugins.title.text) {
      chart.options.plugins.title.display = Boolean(chartTitle);
      chart.options.plugins.title.text = chartTitle;
    }

    if (chartTitle) {
      chart.options.plugins.title.font = {
        family: themeStyles.titleFontFamily,
        size: themeStyles.titleFontSize,
        lineHeight: themeStyles.titleLineHeight
      };
      chart.options.plugins.title.color = themeStyles.titleColor;
    } // Enable disable doughnut center text


    if (doughnutlabel.display !== chart.options.plugins.doughnutlabel.display) {
      chart.options.plugins.doughnutlabel.display = doughnutlabel.display;
    } // Update background color


    if (((_config$styles2 = config.styles) === null || _config$styles2 === void 0 ? void 0 : _config$styles2.backgroundColor) !== chart.options.plugins.customCanvasBackgroundColor.color) {
      var _config$styles3;

      chart.options.plugins.customCanvasBackgroundColor.color = ((_config$styles3 = config.styles) === null || _config$styles3 === void 0 ? void 0 : _config$styles3.backgroundColor) || themeStyles.chartBackgroundColor;
    } // Update data label font


    chart.options.plugins.datalabels.font = {
      family: themeStyles.chartDataLabelFontFamily,
      size: themeStyles.chartDataLabelFontSize,
      lineHeight: themeStyles.chartDataLabelLineHeight
    }; // Update doughnut center labels

    chart.options.plugins.doughnutlabel.labels = doughnutlabel.labels; // Update legend position

    chart.options.plugins.legend.position = getLegendPosition(containerWidth, legendPosition); // Update legend font

    chart.options.plugins.legend.labels.font = {
      family: themeStyles.legendFontFamily,
      size: themeStyles.legendFontSize,
      lineHeight: themeStyles.legendLineHeight
    };
    chart.options.plugins.legend.labels.color = themeStyles.legendColor; // Update legend point style

    chart.options.plugins.legend.labels.usePointStyle = Boolean(themeStyles.chartLegendPointStyle);
    chart.options.plugins.legend.labels.pointStyle = themeStyles.chartLegendPointStyle; // Update editing area when
    // widget is in editing mode

    if (editing) {
      chart.options.onClick = function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        return (// @ts-ignore
          setSliceAsEditingArea({
            widgetId,
            sectionId,
            chartData
          }, ...args)
        );
      };
    }

    chart.data = chartData;
    chart.update(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redrawIndex, chart, containerWidth, (_config$styles4 = config.styles) === null || _config$styles4 === void 0 ? void 0 : _config$styles4.backgroundColor, doughnutLabelRedrawHash, legendPosition, isLoading, chartBackground, animation]);
  const isShowingLoader = !chart;
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({
    ref: containerRef
  }, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      widgetId: widgetId,
      sectionId: sectionId,
      editing: editing,
      config: config,
      className: styles.header
    }), isLoading || hasNoData ? /*#__PURE__*/_jsx(Loading, {
      legendType: legendType,
      isBarsLegend: isBarsLegend,
      editing: editing
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isShowingLoader ? /*#__PURE__*/_jsx(Loading, {
        legendType: legendType,
        isBarsLegend: isBarsLegend,
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsxs("div", {
        className: clsx(styles.main, isBarsLegend ? styles['-barsLegend'] : '', isShowingLoader ? styles['main--hide'] : ''),
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.main__graphic,
          children: /*#__PURE__*/_jsx("canvas", {
            id: getSectionCanvasId(widgetId, sectionId),
            ref: canvasElement,
            className: styles.canvas
          })
        }), canRenderLegend ? /*#__PURE__*/_jsx(BarsLegend, {
          className: styles.main__barsLegend,
          children: legendData.map((item, index) => /*#__PURE__*/_jsx(BarsLegendItem, _objectSpread({
            heading: item.name,
            text: item.template,
            color: item.color,
            amount: item.value,
            maximum: item.maximum,
            containerWidth: containerWidth
          }, editing && openLegendsContextEditor && {
            onClick: e => openLegendsContextEditor(e, widgetId, sectionId)
          }), `bars-legend-${index}`))
        }) : /*#__PURE__*/_jsx(_Fragment, {})]
      })]
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, settings.styles), config.styles || {})
    })]
  }));
};

export default PieDoughnut;
export { settings, selectionConfig, useData, useSelections, DEFAULT_COLORS }; // Determine position of legend based
// on the available space in the container

function getLegendPosition() {
  let containerWidth = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  let legendPosition = arguments.length > 1 ? arguments[1] : undefined;
  if (legendPosition) return `${legendPosition}`;
  return containerWidth > 450 ? 'right' : 'bottom';
} // Apply user selections of an individual
// chart slice as the active editing area


function setSliceAsEditingArea(_ref4, event, activeElements) {
  var _event$native, _event$native2;

  let {
    widgetId,
    sectionId,
    chartData
  } = _ref4;
  if (event.type !== 'click' || activeElements.length === 0) return;
  const [item] = activeElements;
  const dataset = chartData.datasets[item.datasetIndex] || {
    data: []
  };
  const dataItem = dataset.data[item.index] || {
    selection: '',
    selectionIndex: -1
  };
  const {
    selection,
    selectionIndex
  } = dataItem;
  if (selectionIndex < 0) return; // @ts-ignore

  const clientX = event === null || event === void 0 ? void 0 : (_event$native = event.native) === null || _event$native === void 0 ? void 0 : _event$native.clientX; // @ts-ignore

  const clientY = (event === null || event === void 0 ? void 0 : (_event$native2 = event.native) === null || _event$native2 === void 0 ? void 0 : _event$native2.clientY) || null;
  const hasClickPosition = typeof clientX === 'number' && typeof clientY === 'number'; // Send selection

  selectEditingArea({
    widgetId,
    sectionId,
    sectionEditingScope: 'selections',
    sectionEditingArea: `item-${selectionIndex}`,
    clickPosition: hasClickPosition ? {
      x: clientX,
      y: clientY
    } : undefined,
    editingAreaMeta: JSON.stringify({
      selection,
      selectionIndex
    })
  });
}